import { SpriteBatch } from '@cgs/wge';

interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface WebGLResourceTracker {
  textures: Set<WebGLTexture>;
  buffers: Set<WebGLBuffer>;
  programs: Set<WebGLProgram>;
  textureMemory: number;
  bufferMemory: number;
  drawCalls: number;
}

interface SafariAlternativeMetrics {
  frameRate: number;
  lastFrameTime: number;
  frameCount: number;
  canvasSize: { width: number; height: number };
}

export interface PerformanceMonitorParams {
  updateInterval?: number;
  resetBaselineInterval?: number;
  spriteBatch?: SpriteBatch;
}

export class PerformanceMonitor {
  private baseline: number | null = null;
  private memory: PerformanceMemory | null = (window.performance as any).memory || null;
  private memoryMonitor: HTMLDivElement;
  private gpuResourceTracker: WebGLResourceTracker;
  private gl: WebGLRenderingContext | null = null;
  private gpuMemoryExtension: any;
  private safariMetrics: SafariAlternativeMetrics;
  private isSafari: boolean;
  private spriteBatch: SpriteBatch | null;

  constructor({
    updateInterval = 1000,
    resetBaselineInterval = 60000,
    spriteBatch,
  }: PerformanceMonitorParams = {}) {
    this.spriteBatch = spriteBatch ?? null;
    // Определяем Safari
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    this.setBaseline();
    this.memoryMonitor = document.createElement('div');
    this.memoryMonitor.style.position = 'fixed';
    this.memoryMonitor.style.top = '0';
    this.memoryMonitor.style.left = '0';
    this.memoryMonitor.style.width = '180px';
    this.memoryMonitor.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    this.memoryMonitor.style.color = 'white';
    this.memoryMonitor.style.fontSize = '12px';
    this.memoryMonitor.style.fontFamily = 'monospace';
    this.memoryMonitor.style.padding = '10px';
    this.memoryMonitor.style.zIndex = '10000';
    this.memoryMonitor.style.pointerEvents = 'none';
    this.memoryMonitor.style.borderRadius = '5px';
    document.body.appendChild(this.memoryMonitor);

    this.gpuResourceTracker = {
      textures: new Set(),
      buffers: new Set(),
      programs: new Set(),
      textureMemory: 0,
      bufferMemory: 0,
      drawCalls: 0,
    };

    this.safariMetrics = {
      frameRate: 0,
      lastFrameTime: performance.now(),
      frameCount: 0,
      canvasSize: { width: 0, height: 0 },
    };

    this.initGPUMonitoring();
    this.initSafariMetrics();

    setInterval(() => {
      this.renderMemory();
    }, updateInterval);

    setInterval(() => {
      this.setBaseline();
    }, resetBaselineInterval);
  }

  public setBaseline() {
    if (this.memory) {
      this.baseline = this.memory.usedJSHeapSize;
    }
  }

  private initSafariMetrics() {
    if (this.isSafari) {
      // Запускаем мониторинг FPS для Safari
      this.startFPSMonitoring();
    }
  }

  private startFPSMonitoring() {
    const measureFPS = () => {
      this.safariMetrics.frameCount++;
      const currentTime = performance.now();
      const deltaTime = currentTime - this.safariMetrics.lastFrameTime;

      if (deltaTime >= 1000) {
        // каждую секунду
        this.safariMetrics.frameRate = Math.round(
          (this.safariMetrics.frameCount * 1000) / deltaTime
        );
        this.safariMetrics.frameCount = 0;
        this.safariMetrics.lastFrameTime = currentTime;
      }

      requestAnimationFrame(measureFPS);
    };
    requestAnimationFrame(measureFPS);
  }

  private getSafariMetrics(): SafariAlternativeMetrics {
    // Получаем размер canvas
    const canvas = document.querySelector('canvas') as HTMLCanvasElement;
    const canvasSize = canvas
      ? { width: canvas.width, height: canvas.height }
      : { width: 0, height: 0 };

    return {
      frameRate: this.safariMetrics.frameRate,
      lastFrameTime: this.safariMetrics.lastFrameTime,
      frameCount: this.safariMetrics.frameCount,
      canvasSize,
    };
  }

  private initGPUMonitoring() {
    // Попробуем найти WebGL контекст
    const canvas = document.querySelector('canvas') as HTMLCanvasElement;
    if (canvas) {
      this.gl =
        canvas.getContext('webgl') ||
        (canvas.getContext('experimental-webgl') as WebGLRenderingContext);
      if (this.gl) {
        // Получаем расширения для GPU мониторинга
        this.gpuMemoryExtension = this.gl.getExtension('WEBGL_debug_renderer_info');

        // Мониторинг потери WebGL контекста
        canvas.addEventListener('webglcontextlost', (e) => {
          console.warn('WebGL context lost!', e);
        });

        canvas.addEventListener('webglcontextrestored', () => {
          console.log('WebGL context restored');
        });

        // Патчим методы WebGL для трекинга ресурсов
        this.patchWebGLMethods();
      }
    }
  }

  private patchWebGLMethods() {
    if (!this.gl) return;

    const gl = this.gl as any;
    const originalCreateTexture = gl.createTexture.bind(gl);
    const originalDeleteTexture = gl.deleteTexture.bind(gl);
    const originalCreateBuffer = gl.createBuffer.bind(gl);
    const originalDeleteBuffer = gl.deleteBuffer.bind(gl);
    const originalDrawArrays = gl.drawArrays.bind(gl);
    const originalDrawElements = gl.drawElements.bind(gl);

    // Трекинг ресурсов
    gl.createTexture = () => {
      const texture = originalCreateTexture();
      if (texture) {
        this.gpuResourceTracker.textures.add(texture);
      }
      return texture;
    };

    gl.deleteTexture = (texture: any) => {
      if (texture) {
        this.gpuResourceTracker.textures.delete(texture);
      }
      return originalDeleteTexture(texture);
    };

    gl.createBuffer = () => {
      const buffer = originalCreateBuffer();
      if (buffer) {
        this.gpuResourceTracker.buffers.add(buffer);
      }
      return buffer;
    };

    gl.deleteBuffer = (buffer: any) => {
      if (buffer) {
        this.gpuResourceTracker.buffers.delete(buffer);
      }
      return originalDeleteBuffer(buffer);
    };

    // Трекинг draw calls
    gl.drawArrays = (...args: any[]) => {
      this.gpuResourceTracker.drawCalls++;
      return originalDrawArrays(...args);
    };

    gl.drawElements = (...args: any[]) => {
      this.gpuResourceTracker.drawCalls++;
      return originalDrawElements(...args);
    };
  }

  private getGPUInfo() {
    if (!this.gl) return null;

    const info: any = {};

    if (this.gpuMemoryExtension) {
      info.vendor = this.gl.getParameter(this.gpuMemoryExtension.UNMASKED_VENDOR_WEBGL);
      info.renderer = this.gl.getParameter(this.gpuMemoryExtension.UNMASKED_RENDERER_WEBGL);
    }

    info.maxTextureSize = this.gl.getParameter(this.gl.MAX_TEXTURE_SIZE);
    info.maxTextureUnits = this.gl.getParameter(this.gl.MAX_TEXTURE_IMAGE_UNITS);
    info.maxRenderBufferSize = this.gl.getParameter(this.gl.MAX_RENDERBUFFER_SIZE);

    return info;
  }

  public renderMemory() {
    let output = `<strong>Memory Monitor</strong> ${this.isSafari ? '(Safari)' : '(Chrome)'}<br/><br/>`;

    // Chrome - показываем memory API
    if (this.memory && this.baseline) {
      const current = (window.performance as any).memory.usedJSHeapSize;
      const total = (window.performance as any).memory.totalJSHeapSize;
      const limit = (window.performance as any).memory.jsHeapSizeLimit;
      const growth = (current - this.baseline) / 1024 / 1024;

      output += `<div style="margin-bottom: 10px;">
        <strong>JS Memory:</strong><br/>
        Used: ${(current / 1024 / 1024).toFixed(2)} MB<br/>
        Total: ${(total / 1024 / 1024).toFixed(2)} MB<br/>
        Limit: ${(limit / 1024 / 1024).toFixed(2)} MB<br/>
        Growth: ${growth.toFixed(2)} MB
      </div>`;
    }

    // Safari - показываем альтернативные метрики
    if (this.isSafari || !this.memory) {
      const safariMetrics = this.getSafariMetrics();

      output += `<div style="margin-bottom: 10px;">
        <strong>Canvas Performance:</strong><br/>
        FPS: ${safariMetrics.frameRate}<br/>
        Canvas: ${safariMetrics.canvasSize.width}x${safariMetrics.canvasSize.height}<br/>
        ${!this.memory ? '<span style="color: #ffa500;">memory API unavailable</span>' : ''}
      </div>`;
    }

    // Батчи из движка (per-frame)
    if (this.spriteBatch) {
      output += `<div style="margin-bottom: 10px;">
        <strong>Render:</strong><br/>
        Batches: ${this.spriteBatch.batchesCount}<br/>
        Triangles: ${this.spriteBatch.trianglesCount}
      </div>`;
    }

    // GPU информация для всех браузеров
    if (this.gl) {
      const gpuInfo = this.getGPUInfo();
      const textureMemoryMB = (this.gpuResourceTracker.textureMemory / 1024 / 1024).toFixed(2);
      const bufferMemoryMB = (this.gpuResourceTracker.bufferMemory / 1024 / 1024).toFixed(2);

      output += `<div style="margin-bottom: 10px;">
        <strong>GPU Resources:</strong><br/>
        Textures: ${this.gpuResourceTracker.textures.size} (~${textureMemoryMB} MB)<br/>
        Buffers: ${this.gpuResourceTracker.buffers.size} (~${bufferMemoryMB} MB)<br/>
        Programs: ${this.gpuResourceTracker.programs.size}<br/>
        Draw Calls: ${this.gpuResourceTracker.drawCalls}/sec<br/>
        Max Texture Size: ${gpuInfo?.maxTextureSize || 'N/A'}<br/>
        Max Texture Units: ${gpuInfo?.maxTextureUnits || 'N/A'}
      </div>`;

      // Сбрасываем счетчик draw calls каждую секунду
      this.gpuResourceTracker.drawCalls = 0;

      if (gpuInfo?.vendor) {
        output += `<div style="font-size: 10px; color: #ccc;">
          <strong>GPU:</strong> ${gpuInfo.vendor}<br/>
          ${gpuInfo.renderer}
        </div>`;
      }
    }

    this.memoryMonitor.innerHTML = output;
  }
}
